import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isFunction } from 'lodash';
import useClipboardValue from 'hooks/useClipboardValue';
import Keyboard from 'containers/Keyboard';
import { StyledBlock, StyledKeyboardWrapper } from '../StyledComponents';
import ActionsSection from '../ActionsSection';
import innerAnalyticsVolia from '../../../helpers/innerAnalyticsVolia';

const MAX_PASSWORD_LENGTH = 20;

const PasswordForm = (props) => {
  const {
    placeholder,
    invalidMessage,
    buttonLabel,
    sectionName,
    onSubmit,
    children,
    analyticsEventGroup,
    analyticsEventName,
    loginType,
    validation,
    withVisibleKeyboard = false,
  } = props;
  const [password, setPassword] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isValid, setIsValid] = useState(true);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(withVisibleKeyboard);

  const showKeyboard = useCallback(() => setIsKeyboardVisible(true), []);
  const hideKeyboard = useCallback(() => setIsKeyboardVisible(false), []);
  const onPasswordUpdate = useCallback((value) => setPassword(value), []);
  const onCursorPositionUpdate = useCallback((value) => setCursorPosition(value), []);
  const onButtonClick = useCallback(() => {
    const isValid = isFunction(validation) ? validation(password) : !!password;
    if (isValid) {
      onSubmit(password);
    } else {
      innerAnalyticsVolia.postEvent({
        eventGroup: analyticsEventGroup,
        eventName: analyticsEventName,
        eventValue: `login type="${loginType}";"password cannot be empty"`,
      });
    }
    setIsValid(isValid);
  }, [validation, password, onSubmit, analyticsEventGroup, analyticsEventName, loginType]);

  useClipboardValue((value) => {
    if (value.length > MAX_PASSWORD_LENGTH) return;
    setPassword(value);
    setCursorPosition(value.length);
    setIsKeyboardVisible(false);
  });

  return (
    <StyledBlock>
      <ActionsSection sectionName={sectionName} defaultSection>
        <ActionsSection.Input
          value={password}
          cursor={isKeyboardVisible}
          cursorPosition={cursorPosition}
          placeholder={placeholder}
          isValid={isValid}
          invalidMessage={invalidMessage}
          onEnter={showKeyboard}
        />
        <ActionsSection.Button label={buttonLabel} onEnter={onButtonClick} />
        {children}
      </ActionsSection>
      {isKeyboardVisible && (
        <StyledKeyboardWrapper>
          <Keyboard
            onQueryUpdated={onPasswordUpdate}
            onCursorPositionUpdate={onCursorPositionUpdate}
            onLeave={hideKeyboard}
            query={password}
            maxQuerySize={MAX_PASSWORD_LENGTH}
            selectedLanguage="en"
            type="noEmbeddedInput"
          />
        </StyledKeyboardWrapper>
      )}
    </StyledBlock>
  );
};

PasswordForm.defaultProps = {
  sectionName: 'CONTRACT_NUMBER_INPUT',
  initialValue: '',
};

PasswordForm.propTypys = {
  onSubmit: PropTypes.func.isRequired,
  buttonLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  invalidMessage: PropTypes.string,
  sectionName: PropTypes.string,
  children: PropTypes.node,
};

export default PasswordForm;
